/* eslint-disable react/prop-types */
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { ActionButton } from '@augmentt-dev/ui-components'
import CopyButton from '../../../../../partials/content/CopyButton'
import DetailRow from '../../../../../components/DetailRow'
import { formatAddress } from '../utils'
import EmployeeActionsSection from '../EmployeeActionsSection'
import PopoverMenu from '../../../../../components/PopoverMenu'
import useIsAuthorized from '../../../ThreatAlerts/useIsAuthorized'
import useCustomerIntegration from '../../../../../hooks/useCustomerIntegration'
import { formatDateOnly } from '../../../../../util/date.util'

function UserProperties(props) {
  const {
    employee,
    selectedCustomer,
    onEdit,
    overrideEngageLicensing = false,
    canPerformActions = true,
    setAction,
    setModal,
    openIntegrationModal,
  } = props

  const intl = useIntl()

  // Get integration app name for authorization
  const customerIntegration = useCustomerIntegration({
    selectedCustomerId: selectedCustomer?.id,
  })
  const integrationAppName = customerIntegration?.IntegrationApplication?.application_name
  const isIntegrationAuthorized = useIsAuthorized({
    integrationAppName,
    selectedCustomerId: selectedCustomer?.id,
  })
  const isAuthorized = canPerformActions && isIntegrationAuthorized

  return (
    <>
      {/* Action Buttons Row */}
      <div
        style={{
          paddingLeft: '0.75rem',
          paddingRight: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid var(--aug-ui-divider-subtle)',
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: '0.375rem' }}>
          <ActionButton
            icon="fas fa-pen-to-square"
            label={<FormattedMessage id="GENERAL.EDIT" />}
            variant="primary"
            disabled={!canPerformActions}
            onClick={() => onEdit()}
          />
          {employee?.employeeIdentifier && (
            <EmployeeActionsSection
              isAuthorized={isAuthorized}
              openIntegrationModal={openIntegrationModal}
              selectedEmployees={[employee]}
              overrideEngageLicensing={overrideEngageLicensing}
            />
          )}
          {employee?.employeeIdentifier && canPerformActions && (
            <PopoverMenu
              config={[
                {
                  title: 'ENGAGE.SECURITY_ACTIONS',
                  items: [
                    { id: 'revoke-sessions', label: 'ENGAGE.SIGN_OUT_ALL_APPS_NO_HYPHEN' },
                    { id: 're-register-mfa' },
                    { id: 'temporary-access-pass' },
                  ],
                },
                {
                  title: 'ENGAGE.MANAGEMENT_ACTIONS',
                  items: [
                    {
                      id: 'groups',
                      label: 'ENGAGE.GROUPS',
                      onClick: () => {}, // No action, just expand sub-menu
                      subMenu: {
                        title: 'ENGAGE.EDIT_GROUPS',
                        items: [
                          { id: 'manage-groups', label: 'ENGAGE.M365' },
                          { id: 'manage-security-groups', label: 'ENGAGE.SECURITY' },
                          { id: 'manage-distribution-mailboxes', label: 'ENGAGE.DISTRIBUTION_MAIL_ENABLED_SECURITY' },
                        ],
                      },
                    },
                    { id: 'manage-licenses' },
                    { id: 'out-of-office' },
                    { id: 'global-address-list' },
                    { id: 'manage-email-forwarding' },
                    { id: 'manage-shared-mailboxes', label: 'ENGAGE.SHARED_MAILBOX_ACCESS' },
                    { id: 'offboard-user' },
                  ],
                },
              ]}
              employee={employee}
              context={{
                isAuthorized,
                openIntegrationModal,
                selectedIntegration: customerIntegration,
                integrationType: integrationAppName === 'Office365' ? 'Office365' : 'GWS',
                setAction,
                setModal,
                actionConditions: {
                  'global-address-list': (emp) => ({
                    isAvailable: emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox,
                  }),
                  'manage-email-forwarding': (emp) => ({
                    isAvailable: emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox,
                  }),
                },
              }}
              toggleProps={{
                icon: 'fas fa-ellipsis',
                iconStyle: { color: 'var(--aug-act-icon)' },
                variant: 'light',
                style: { height: '28px', padding: '0 9px' },
              }}
            />
          )}
        </div>
      </div>

      <div className="section px-4 py-4">
        {/* Identity Section */}
        <Row className="mb-4">
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Identity
            </h6>

            <DetailRow label="Display name" value={employee.display_name} size="sm" />

            <DetailRow label="First name" value={employee.first_name} size="sm" />

            <DetailRow label="Last name" value={employee.last_name} size="sm" />

            <DetailRow label="User type" value={employee.userType} size="sm" />

            <DetailRow
              label="Object ID"
              value={
                employee.employeeIdentifier ? (
                  <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                    <span>{employee.employeeIdentifier}</span>
                    <CopyButton textToCopy={employee.employeeIdentifier} />
                  </div>
                ) : null
              }
              size="sm"
            />
          </Col>
        </Row>

        {/* Contact Information Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Contact Information
            </h6>

            <DetailRow label="Address" value={formatAddress(employee)} size="sm" />

            <DetailRow label="Country/Region" value={employee.usageLocation} size="sm" />

            <DetailRow label="Business phone" value={employee.businessPhone || employee.phone} size="sm" />

            <DetailRow label="Mobile phone" value={employee.mobilePhone} size="sm" />

            <DetailRow label="Email" value={employee.email} size="sm" />

            <DetailRow
              label="Alternate email"
              value={employee.otherMails && employee.otherMails.length > 0 ? employee.otherMails.join(', ') : null}
              size="sm"
            />

            <DetailRow label="Fax" value={employee.fax} size="sm" />

            <DetailRow label="Mail nickname" value={employee.mailNickname} size="sm" />
          </Col>
        </Row>

        {/* Job Information Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Job Information
            </h6>

            <DetailRow label="Job title" value={employee.job_title} size="sm" />

            <DetailRow label="Company name" value={employee.companyName} size="sm" />

            <DetailRow label="Department" value={employee.department} size="sm" />

            <DetailRow label="Employee ID" value={employee.employeeId} size="sm" />

            <DetailRow label="Employee type" value={employee.employeeType} size="sm" />

            <DetailRow label="Employee hire date" value={formatDateOnly(employee.hireDate, intl)} size="sm" />

            <DetailRow label="Office location" value={employee.officeLocation} size="sm" />

            <DetailRow label="Manager" value={employee.manager?.displayName} size="sm" />
          </Col>
        </Row>

        {/* Settings Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Settings
            </h6>

            <DetailRow
              label="Account enabled"
              value={(() => {
                if (employee.isUserSigninEnabled !== undefined) {
                  return employee.isUserSigninEnabled ? 'Yes' : 'No'
                }
                if (employee.enabled !== undefined) {
                  return employee.enabled ? 'Yes' : 'No'
                }
                return null
              })()}
              size="sm"
            />

            <DetailRow label="Usage location" value={employee.usageLocation} size="sm" />
          </Col>
        </Row>
      </div>
    </>
  )
}

export default UserProperties
